import { SideDrawer } from "@/components/common/SideDrawer";
import { CompactTaskList } from "@/resources/tasks/components/task-list/CompactTaskList";
import { TaskDetailsDialog } from "@/resources/tasks/components/task-dialogs/TaskDetailsDialog";
import { useState } from "react";
import { useRecordContext } from "react-admin";
import { TaskComposerInline } from "../../tasks/components/task-list/TaskComposerInline";
import { ObjectType, Task, TaskRecordReference } from "@/types";
import { Box, Typography, Collapse, ButtonBase } from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";

interface ClaimantSideDrawerProps {
  drawerExpanded: boolean;
  toggleDrawer: () => void;
  /** Shift left by this many px so the drawer stacks beside the document preview drawer. */
  offsetRight?: number;
}

export const ClaimantSideDrawer: React.FC<ClaimantSideDrawerProps> = ({
  drawerExpanded,
  toggleDrawer,
  offsetRight = 0,
}) => {
  const claimant = useRecordContext();
  const [refreshKey, setRefreshKey] = useState(0);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Task | undefined>();
  const [showCompleted, setShowCompleted] = useState(false);

  if (!claimant) return null;

  const recordRef = {
    object_type: ObjectType.CLAIMANTS as const,
    object_id: claimant.id,
    label: claimant.claimant_name,
  } as TaskRecordReference;

  const handleTaskClick = (task: Task) => {
    setSelected(task);
    setOpen(true);
  };

  return (
    <>
      <SideDrawer title="Tasks" open={drawerExpanded} onClose={toggleDrawer} offsetRight={offsetRight} hideHandle>
        {/* Inline add, bound to this claimant */}
        <TaskComposerInline record={recordRef} onCreated={() => setRefreshKey((k) => k + 1)} open={drawerExpanded} />

        {/* Open tasks */}
        <CompactTaskList
          key={`open-${refreshKey}`}
          recordRef={recordRef}
          hideCompleted
          onTaskClick={handleTaskClick}
          showAssignedUser={true}
          showRelatedTo={false}
        />

        {/* Completed tasks — expandable section */}
        <Box sx={{ mt: 1, borderTop: 1, borderColor: "divider" }}>
          <ButtonBase
            onClick={() => setShowCompleted(!showCompleted)}
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1.5,
              py: 1,
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Completed Tasks
            </Typography>
            <ExpandMoreIcon
              sx={{
                fontSize: 18,
                color: "text.disabled",
                transition: "transform 0.2s",
                transform: showCompleted ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </ButtonBase>
          <Collapse in={showCompleted} timeout="auto" unmountOnExit>
            <CompactTaskList
              key={`completed-${refreshKey}`}
              recordRef={recordRef}
              statusFilter="completed"
              onTaskClick={handleTaskClick}
              showAssignedUser={true}
              showRelatedTo={false}
              showCheckbox={false}
            />
          </Collapse>
        </Box>
      </SideDrawer>

      <TaskDetailsDialog open={open} task={selected} onClose={() => setOpen(false)} />
    </>
  );
};
